import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../Spinner';
import NotifyPaymentModal from './NotifyPaymentModal';
import MarkPaidButton from './MarkPaidButton';
import { BillingInfoHeader, isCouponMethod } from './BillingInfoHeader';

function PaymentStatusBadge({ liquidation }) {
    const { t } = useTranslation();
    const isPaid = liquidation.payment_status_manual === 'Pago' || liquidation.payment_status_auto === 'Pago';

    if (isPaid) {
        return (
            <span style={{
                backgroundColor: '#7CB502',
                color: '#fff',
                padding: '0.15em 0.5em',
                borderRadius: '0.3em',
                fontSize: '0.75em',
                fontWeight: 600,
                fontFamily: 'Source Sans Pro, sans-serif'
            }}>
                {t('agency_subscription.paid')}
            </span>
        );
    }

    return (
        <span style={{
            backgroundColor: '#EC9F00',
            color: '#fff',
            padding: '0.15em 0.5em',
            borderRadius: '0.3em',
            fontSize: '0.75em',
            fontWeight: 600,
            fontFamily: 'Source Sans Pro, sans-serif'
        }}>
            {t('agency_subscription.pending_payment')}
        </span>
    );
}

function CouponStatusCell({ cupons }) {
    const { t } = useTranslation();

    if (!cupons || cupons.length === 0) {
        return <span style={{ color: '#B6B6B7' }}>-</span>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3em' }}>
            {cupons.map((cupon, idx) => {
                const statusColors = {
                    paid: '#7CB502',
                    pending: '#EC9F00',
                    cancelled: '#B6B6B7'
                };
                const statusLabels = {
                    paid: t('agency_subscription.paid'),
                    pending: t('agency_subscription.pending_payment'),
                    cancelled: t('agency_subscription.coupon_cancelled')
                };
                const color = statusColors[cupon.simplified_status] || '#B6B6B7';
                const label = statusLabels[cupon.simplified_status] || cupon.status;

                return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                        <span style={{
                            backgroundColor: color,
                            color: '#fff',
                            padding: '0.1em 0.4em',
                            borderRadius: '0.2em',
                            fontSize: '0.7em',
                            fontWeight: 600
                        }}>
                            {label}
                        </span>
                        {cupon.simplified_status === 'pending' && cupon.payment_url && (
                            <a
                                href={cupon.payment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: '#FF470F',
                                    fontSize: '0.8em',
                                    fontWeight: 600,
                                    textDecoration: 'none'
                                }}
                            >
                                {t('agency_subscription.pay_now')}
                            </a>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function formatArgAmount(num, currency) {
    if (num == null) return '-';
    const prefix = currency === 'ARS' ? '$ ' : 'US$ ';
    return prefix + Number(num).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function BillingHistorySection({ billingData, onRetry, onEmailsUpdated, showPaymentDetails = false, onLiquidationUpdated }) {
    const { t } = useTranslation();

    if (!billingData) {
        return (
            <div className="box" style={{ marginBottom: '1.5em' }}>
                <header className="box-header">
                    <h2 className="box-title">{t('agency_subscription.billing_history')}</h2>
                </header>
                <div style={{ padding: '2em', textAlign: 'center' }}>
                    <Spinner color="positive small" />
                </div>
            </div>
        );
    }

    if (!billingData.available) {
        return (
            <div className="box" style={{ marginBottom: '1.5em' }}>
                <header className="box-header">
                    <h2 className="box-title">{t('agency_subscription.billing_history')}</h2>
                </header>
                <div style={{
                    padding: '2em 1.1em',
                    textAlign: 'center',
                    color: '#B6B6B7',
                    fontFamily: 'Source Sans Pro, sans-serif',
                    fontSize: '0.87em'
                }}>
                    <p style={{ margin: '0 0 0.75em' }}>
                        {billingData.reason === 'tesla_error'
                            ? t('agency_subscription.billing_error')
                            : t('agency_subscription.billing_not_available')
                        }
                    </p>
                    {billingData.reason === 'tesla_error' && onRetry && (
                        <button className="btn -alt" onClick={onRetry} style={{ fontSize: '0.87em' }}>
                            {t('agency_subscription.retry')}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const { active_comm_agree, liquidations } = billingData;
    const showCouponColumn = showPaymentDetails && active_comm_agree && isCouponMethod(active_comm_agree.payment_method);

    return (
        <div className="box" style={{ marginBottom: '1.5em' }}>
            <header className="box-header">
                <h2 className="box-title">{t('agency_subscription.billing_history')}</h2>
            </header>
            <div style={{ padding: '0.75em 1.1em 1.1em' }}>
                {showPaymentDetails ? (
                    <BillingInfoHeader
                        billingData={billingData}
                        onEmailsUpdated={onEmailsUpdated}
                    />
                ) : (
                    active_comm_agree && Object.keys(active_comm_agree).length > 0 && (
                        <div style={{
                            backgroundColor: '#F9F9F9',
                            borderRadius: '0.3em',
                            padding: '0.85em 1em',
                            marginBottom: '1em'
                        }}>
                            <h6 style={{
                                fontFamily: 'Source Sans Pro, sans-serif',
                                fontWeight: 600,
                                fontSize: '0.87em',
                                color: '#4A4A4A',
                                marginBottom: '0.4em'
                            }}>
                                {t('agency_subscription.active_agreement')}
                            </h6>
                            <div style={{
                                display: 'flex',
                                gap: '2em',
                                flexWrap: 'wrap',
                                fontSize: '0.87em',
                                fontFamily: 'Source Sans Pro, sans-serif'
                            }}>
                                {active_comm_agree.currency && (
                                    <div><span style={{ color: '#B6B6B7' }}>{t('agency_subscription.currency')}:</span> <strong style={{ color: '#4A4A4A' }}>{active_comm_agree.currency}</strong></div>
                                )}
                                {active_comm_agree.payment_method && (
                                    <div><span style={{ color: '#B6B6B7' }}>{t('agency_subscription.payment_method_label')}:</span> <strong style={{ color: '#4A4A4A' }}>{active_comm_agree.payment_method}</strong></div>
                                )}
                                {active_comm_agree.fee_text && (
                                    <div><span style={{ color: '#B6B6B7' }}>{t('agency_subscription.fees')}:</span> <strong style={{ color: '#4A4A4A' }}>{active_comm_agree.fee_text}</strong></div>
                                )}
                            </div>
                        </div>
                    )
                )}

                {(!liquidations || liquidations.length === 0) ? (
                    <p style={{
                        textAlign: 'center',
                        color: '#B6B6B7',
                        padding: '1.5em 0',
                        fontFamily: 'Source Sans Pro, sans-serif',
                        fontSize: '0.87em'
                    }}>
                        {t('agency_subscription.no_liquidations')}
                    </p>
                ) : (
                    <div className="table-box">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>{t('agency_subscription.month')}</th>
                                    <th style={thStyle}>{t('agency_subscription.payment_status')}</th>
                                    {showCouponColumn && (
                                        <th style={thStyle}>{t('agency_subscription.coupon_status')}</th>
                                    )}
                                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('agency_subscription.amount')}</th>
                                    <th style={thStyle}>{t('agency_subscription.invoice')}</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>{t('agency_subscription.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liquidations.map((liq, idx) => {
                                    const isPaid = liq.payment_status_manual === 'Pago' || liq.payment_status_auto === 'Pago';
                                    const rowBg = idx % 2 === 0 ? '#fff' : '#F7F7F7';
                                    return (
                                        <tr key={liq.id} style={{ backgroundColor: rowBg }}>
                                            <td style={{ ...tdStyle, fontWeight: 600 }}>{liq.month_created}</td>
                                            <td style={tdStyle}><PaymentStatusBadge liquidation={liq} /></td>
                                            {showCouponColumn && (
                                                <td style={tdStyle}>
                                                    <CouponStatusCell cupons={liq.payment_cupons} />
                                                </td>
                                            )}
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                                                {formatArgAmount(liq.invoice_total, liq.currency)}
                                            </td>
                                            <td style={tdStyle}>
                                                {liq.colppy_invoice_numbers || '-'}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {!isPaid && (
                                                    <div style={{ display: 'flex', gap: '0.4em', justifyContent: 'center', alignItems: 'center' }}>
                                                        <NotifyPaymentModal
                                                            liquidationId={liq.id}
                                                            liquidationMonth={liq.month_created}
                                                        />
                                                        {showPaymentDetails && (
                                                            <MarkPaidButton
                                                                liquidationId={liq.id}
                                                                onMarked={onLiquidationUpdated}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const thStyle = {
    color: '#4A4A4A',
    fontSize: '0.75em',
    fontWeight: 600,
    fontFamily: 'Source Sans Pro, sans-serif',
    height: '3.5em',
    padding: '0 1.25em',
    textAlign: 'left',
    borderBottom: '1px solid #E2E2E2'
};

const tdStyle = {
    color: '#4A4A4A',
    fontSize: '0.87em',
    fontWeight: 400,
    fontFamily: 'Source Sans Pro, sans-serif',
    height: '2.8em',
    padding: '0.4em 1.25em'
};
